
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Eye, Loader2 } from 'lucide-react';
import { useCommunity } from '@/contexts/CommunityContext';
import { usePreviewUsers, usePreviewBusinesses, type PreviewUser, type PreviewBusiness } from '@/hooks/usePreviewData';
import { usePreviewServiceRequests } from '@/hooks/usePreviewServiceRequests';
import { usePreviewReviews } from '@/hooks/usePreviewReviews';
import { usePreviewConversations } from '@/hooks/usePreviewConversations';
import { usePreviewJobProposals, type PreviewJobProposal } from '@/hooks/usePreviewJobProposals';
import { useTemplateRequirements } from '@/hooks/useTemplateRequirements';
import { useEmailPreview } from '@/hooks/useEmailPreview';
import type { EmailTemplate } from '@/hooks/useEmailTemplates';
import type { CompletePreviewPayload } from '@/types/email';

interface EmailPreviewProps {
  template: EmailTemplate;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const EmailPreview = ({ template, open, onOpenChange }: EmailPreviewProps) => {
  const { currentCommunity } = useCommunity();
  
  // Get template requirements
  const { requirements, isLoading: requirementsLoading, error: requirementsError } = useTemplateRequirements(template.template_type);
  
  // Get required entities from template requirements
  const requiredEntities = useMemo(() => {
    const entities = requirements?.required_entities || [];
    return entities;
  }, [requirements, template.template_type]);

  // Conditional data fetching hooks - only fetch what's needed
  const { data: users, isLoading: usersLoading } = usePreviewUsers(requiredEntities.includes('user'));
  const { data: businesses, isLoading: businessesLoading } = usePreviewBusinesses(requiredEntities.includes('business'));
  const { data: serviceRequests, isLoading: serviceRequestsLoading } = usePreviewServiceRequests(
    currentCommunity?.id, 
    requiredEntities.includes('service_request')
  );
  const { data: reviews, isLoading: reviewsLoading } = usePreviewReviews(
    currentCommunity?.id,
    requiredEntities.includes('review')
  );
  const { data: conversations, isLoading: conversationsLoading } = usePreviewConversations(
    currentCommunity?.id,
    requiredEntities.includes('conversation')
  );
  const { data: jobProposals, isLoading: jobProposalsLoading } = usePreviewJobProposals(
    currentCommunity?.id,
    requiredEntities.includes('job_proposal')
  );
  const { getPreview, isLoading: previewLoading } = useEmailPreview();

  
  // Selection states
  const [selectedUser, setSelectedUser] = useState<string>('');
  const [selectedBusiness, setSelectedBusiness] = useState<string>('');
  const [selectedServiceRequest, setSelectedServiceRequest] = useState<string>('');
  const [selectedReview, setSelectedReview] = useState<string>('');
  const [selectedConversation, setSelectedConversation] = useState<string>('');
  const [selectedJobProposal, setSelectedJobProposal] = useState<string>('');
  const [processedEmail, setProcessedEmail] = useState<{
    subject: string;
    html: string;
    text?: string;
    sender_email?: string;
  }>({ subject: '', html: '', text: '' });
  const [isInitializing, setIsInitializing] = useState(false);

  // Build complete preview payload based on selected options
  const buildCompletePayload = useCallback((): CompletePreviewPayload | null => {
    if (!currentCommunity) return null;

    const user = users?.find(u => u.id === selectedUser);
    const business = businesses?.find(b => b.id === selectedBusiness);
    const serviceRequest = serviceRequests?.find(sr => sr.id === selectedServiceRequest);
    const review = reviews?.find(r => r.id === selectedReview);
    const conversation = conversations?.find(c => c.id === selectedConversation);
    const jobProposal = jobProposals?.find(jp => jp.id === selectedJobProposal);


    const payload: CompletePreviewPayload = {
      template_type: template.template_type,
      community_id: currentCommunity.id,
    };

    // Add user data if selected and required
    if (user && requiredEntities.includes('user')) {
      payload.user_id = user.id;
      payload.user_email = user.email;
      payload.user_name = `${user.firstname} ${user.lastname}`.trim();
      payload.user_firstname = user.firstname;
      payload.user_lastname = user.lastname;
    }

    // Add business data if selected and required
    if (business && requiredEntities.includes('business')) {
      payload.business_id = business.id;
      payload.business_name = business.name;
      payload.business_owner_name = `${business.user_profiles.firstname} ${business.user_profiles.lastname}`.trim();
      payload.business_owner_email = business.user_profiles.email;
      payload.business_owner_firstname = business.user_profiles.firstname;
      payload.business_owner_lastname = business.user_profiles.lastname;
      payload.business_description = business.description || '';
      payload.business_category = '';
      payload.contact_email = business.contact_email || business.user_profiles.email || '';
      payload.contact_phone = business.contact_phone || '';
      payload.full_address = business.full_address || '';
      payload.website = business.website || '';
      payload.facebook_url = business.facebook_url || '';
      payload.instagram_url = business.instagram_url || '';
      payload.tiktok_url = business.tiktok_url || '';
      payload.google_maps_url = business.google_maps_url || '';
      payload.application_comments = business.application_comments || '';
      payload.registration_date = business.created_at || '';
      
      // For business templates that need user_name (like business_listed), set it to the business owner's name
      if (!payload.user_name && requiredEntities.includes('user')) {
        payload.user_id = business.user_id;
        payload.user_email = business.user_profiles.email;
        payload.user_name = `${business.user_profiles.firstname} ${business.user_profiles.lastname}`.trim();
        payload.user_firstname = business.user_profiles.firstname;
        payload.user_lastname = business.user_profiles.lastname;
      }
    }

    // Add service request data if selected and required
    if (serviceRequest && requiredEntities.includes('service_request')) {
      payload.service_request_id = serviceRequest.id;
      payload.service_request_title = serviceRequest.original_request; // Map original_request to title
      payload.service_request_description = serviceRequest.original_request; // Use same for description
      payload.service_request_summary = serviceRequest.original_request; // Use same for summary
      payload.service_location = serviceRequest.town || 'Location not specified';
      payload.service_urgency = serviceRequest.urgency || 'normal';
      // Note: user_comment doesn't exist in service_requests table
      // If we have user data from service request
      if (!payload.user_firstname && serviceRequest.user_firstname) {
        payload.user_firstname = serviceRequest.user_firstname;
        payload.user_lastname = serviceRequest.user_lastname;
        payload.user_name = `${serviceRequest.user_firstname} ${serviceRequest.user_lastname}`.trim();
        payload.user_id = serviceRequest.user_id;
      }
    }

    // Add review data if selected and required
    if (review && requiredEntities.includes('review')) {
      payload.review_id = review.id;
      payload.review_rating = review.rating;
      payload.review_comment = review.description; // Map description to review_comment
      payload.review_date = review.created_at;
      payload.reviewer_id = review.user_id;  // Use user_id from reviews table
      payload.reviewer_name = `${review.reviewer_firstname} ${review.reviewer_lastname}`.trim();
      payload.reviewer_firstname = review.reviewer_firstname;
      payload.reviewer_lastname = review.reviewer_lastname;
      // If we have business data from review
      if (!payload.business_id && review.business_id) {
        payload.business_id = review.business_id;
        payload.business_name = review.business_name;
      }
    }

    // Add conversation data if selected and required
    if (conversation && requiredEntities.includes('conversation')) {
      payload.conversation_id = conversation.id;
      payload.message_content = conversation.latest_message_content;
      payload.conversation_history = conversation.messages.map(m => 
        `<div><strong>Message:</strong> ${m.content}</div>`
      ).join('');
      // Use member and business names for sender/receiver
      payload.sender_name = conversation.member_name;
      payload.receiver_name = conversation.business_name;
    }

    // Add job proposal data if selected and required
    if (jobProposal && requiredEntities.includes('job_proposal')) {
      payload.job_proposal_id = jobProposal.id;
      payload.service_request_id = jobProposal.service_request_id;
      payload.business_id = jobProposal.business_id;
      payload.service_request_title = jobProposal.service_request_title;
      payload.service_request_description = jobProposal.service_request_title;
      payload.service_request_summary = jobProposal.service_request_title;
      payload.business_name = jobProposal.business_name;
      payload.proposal_status = jobProposal.status; // Add proposal status from job proposal
      // If we don't have user data from other sources, use job proposal user data
      if (!payload.user_firstname && jobProposal.user_firstname) {
        payload.user_firstname = jobProposal.user_firstname;
        payload.user_lastname = jobProposal.user_lastname;
        payload.user_name = `${jobProposal.user_firstname} ${jobProposal.user_lastname}`.trim();
      }
    }

    return payload;
  }, [currentCommunity, template.template_type, users, businesses, serviceRequests, reviews, conversations, jobProposals,
      selectedUser, selectedBusiness, selectedServiceRequest, selectedReview, selectedConversation, selectedJobProposal, requiredEntities]);

  // Fetch preview when inputs change
  useEffect(() => {
    const fetchPreview = async () => {
      try {
        const payload = buildCompletePayload();
        if (!payload) return;

        const preview = await getPreview(payload);
        if (preview) {
          setProcessedEmail({
            subject: preview.subject,
            html: preview.html,
            text: preview.text,
            sender_email: preview.sender_email
          });
        }
      } catch (error) {
        // Don't retry on error to prevent infinite loop
      }
    };

    // Only fetch if dialog is open, requirements are loaded, all required data is selected, and not already loading or initializing
    if (open && !previewLoading && !requirementsLoading && !isInitializing && requirements && template.template_type && currentCommunity?.id) {
      // Check if we have the minimum required selections
      const hasRequiredSelections = requiredEntities.every(entity => {
        switch (entity) {
          case 'user': return selectedUser;
          case 'business': return selectedBusiness;
          case 'service_request': return selectedServiceRequest;
          case 'review': return selectedReview;
          case 'conversation': return selectedConversation;
          case 'job_proposal': return selectedJobProposal;
          default: return true;
        }
      });

      if (hasRequiredSelections) {
        fetchPreview();
      }
    }
  }, [
    open, 
    template.template_type,
    currentCommunity?.id,
    selectedUser,
    selectedBusiness, 
    selectedServiceRequest,
    selectedReview,
    selectedConversation,
    selectedJobProposal,
    requirementsLoading,
    requirements,
    isInitializing,
    // Remove buildCompletePayload and getPreview from dependencies to prevent infinite loop
    // requiredEntities included as JSON string to avoid reference issues
    JSON.stringify(requiredEntities)
  ]);

  const isLoading = requirementsLoading || usersLoading || businessesLoading || serviceRequestsLoading || 
                   reviewsLoading || conversationsLoading || jobProposalsLoading || previewLoading;

  // Check if we have all required data loaded for the required entities
  const hasRequiredDataLoaded = !requirementsLoading && requiredEntities.every(entity => {
    switch (entity) {
      case 'user': return !usersLoading;
      case 'business': return !businessesLoading;
      case 'service_request': return !serviceRequestsLoading;
      case 'review': return !reviewsLoading;
      case 'conversation': return !conversationsLoading;
      case 'job_proposal': return !jobProposalsLoading;
      default: return true;
    }
  });

  // Reset state when modal opens
  React.useEffect(() => {
    if (open) {
      setIsInitializing(true);
      // Clear previous selections
      setSelectedUser('');
      setSelectedBusiness('');
      setSelectedServiceRequest('');
      setSelectedReview('');
      setSelectedConversation('');
      setSelectedJobProposal('');
      setProcessedEmail({ subject: '', html: '', text: '' });
    } else {
      setIsInitializing(false);
    }
  }, [open, template.template_type]);

  // Auto-select first available options when data is ready and modal is open
  React.useEffect(() => {
    if (!open || !hasRequiredDataLoaded || !isInitializing) return;

    let hasSelections = false;

    // Auto-select required entities that don't have selections yet
    if (requiredEntities.includes('user') && users?.length && !selectedUser) {
      setSelectedUser(users[0].id);
      hasSelections = true;
    }

    if (requiredEntities.includes('business') && businesses?.length && !selectedBusiness) {
      setSelectedBusiness(businesses[0].id);
      hasSelections = true;
    }

    if (requiredEntities.includes('service_request') && serviceRequests?.length && !selectedServiceRequest) {
      setSelectedServiceRequest(serviceRequests[0].id);
      hasSelections = true;
    }

    if (requiredEntities.includes('review') && reviews?.length && !selectedReview) {
      setSelectedReview(reviews[0].id);
      hasSelections = true;
    }

    if (requiredEntities.includes('conversation') && conversations?.length && !selectedConversation) {
      setSelectedConversation(conversations[0].id);
      hasSelections = true;
    }

    if (requiredEntities.includes('job_proposal') && jobProposals?.length && !selectedJobProposal) {
      setSelectedJobProposal(jobProposals[0].id);
      hasSelections = true;
    }

    // Mark initialization as complete after a short delay to allow state updates
    if (hasSelections || requiredEntities.length === 0) {
      setTimeout(() => setIsInitializing(false), 100);
    }
  }, [
    open,
    hasRequiredDataLoaded,
    isInitializing,
    requiredEntities,
    users,
    businesses,
    serviceRequests,
    reviews,
    conversations,
    jobProposals,
    selectedUser,
    selectedBusiness,
    selectedServiceRequest,
    selectedReview,
    selectedConversation,
    selectedJobProposal
  ]);

  // Check if we're still in the process of auto-selecting required entities
  const isAutoSelecting = open && !requirementsLoading && requiredEntities.some(entity => {
    switch (entity) {
      case 'user': return users?.length && !selectedUser;
      case 'business': return businesses?.length && !selectedBusiness;
      case 'service_request': return serviceRequests?.length && !selectedServiceRequest;
      case 'review': return reviews?.length && !selectedReview;
      case 'conversation': return conversations?.length && !selectedConversation;
      case 'job_proposal': return jobProposals?.length && !selectedJobProposal;
      default: return false;
    }
  });

  // Only show content when data is loaded, auto-selection is complete, and initialization is done
  const isContentReady = hasRequiredDataLoaded && !isAutoSelecting && !isInitializing;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5" />
            Email Preview: {template.template_type.replace('_', ' ').toUpperCase()}
          </DialogTitle>
        </DialogHeader>

        {!isContentReady ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
            <span className="ml-2">
              {isAutoSelecting ? 'Setting up preview...' : 'Loading preview data...'}
            </span>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Entity Selection Controls */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4 bg-muted rounded-lg">
              <h3 className="col-span-full text-sm font-medium">
                Select Data for Preview ({template.template_type}):
              </h3>
              
              {requiredEntities.includes('user') && (
                <div className="space-y-2">
                  <Label htmlFor="user-select">User</Label>
                  <Select value={selectedUser} onValueChange={setSelectedUser}>
                    <SelectTrigger id="user-select">
                      <SelectValue placeholder="Select a user" />
                    </SelectTrigger>
                    <SelectContent>
                      {users?.map(user => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.firstname} {user.lastname} ({user.email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {requiredEntities.includes('business') && (
                <div className="space-y-2">
                  <Label htmlFor="business-select">Business</Label>
                  <Select value={selectedBusiness} onValueChange={setSelectedBusiness}>
                    <SelectTrigger id="business-select">
                      <SelectValue placeholder="Select a business" />
                    </SelectTrigger>
                    <SelectContent>
                      {businesses?.map(business => (
                        <SelectItem key={business.id} value={business.id}>
                          {business.name} ({business.user_profiles.firstname} {business.user_profiles.lastname})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {requiredEntities.includes('service_request') && (
                <div className="space-y-2">
                  <Label htmlFor="service-request-select">Service Request</Label>
                  <Select value={selectedServiceRequest} onValueChange={setSelectedServiceRequest}>
                    <SelectTrigger id="service-request-select">
                      <SelectValue placeholder="Select a service request" />
                    </SelectTrigger>
                    <SelectContent>
                      {serviceRequests?.map(request => (
                        <SelectItem key={request.id} value={request.id}>
                          {request.original_request.substring(0, 50)}... - {request.town} ({request.urgency})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {requiredEntities.includes('review') && (
                <div className="space-y-2">
                  <Label htmlFor="review-select">Review</Label>
                  <Select value={selectedReview} onValueChange={setSelectedReview}>
                    <SelectTrigger id="review-select">
                      <SelectValue placeholder="Select a review" />
                    </SelectTrigger>
                    <SelectContent>
                      {reviews?.map(review => (
                        <SelectItem key={review.id} value={review.id}>
                          {review.rating}⭐ by {review.reviewer_firstname} {review.reviewer_lastname} for {review.business_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {requiredEntities.includes('conversation') && (
                <div className="space-y-2">
                  <Label htmlFor="conversation-select">Conversation</Label>
                  <Select value={selectedConversation} onValueChange={setSelectedConversation}>
                    <SelectTrigger id="conversation-select">
                      <SelectValue placeholder="Select a conversation" />
                    </SelectTrigger>
                    <SelectContent>
                      {conversations?.map(conversation => (
                        <SelectItem key={conversation.id} value={conversation.id}>
                          {conversation.member_name} & {conversation.business_name} ({conversation.message_count} messages)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {requiredEntities.includes('job_proposal') && (
                <div className="space-y-2">
                  <Label htmlFor="job-proposal-select">Job Proposal</Label>
                  <Select value={selectedJobProposal} onValueChange={setSelectedJobProposal}>
                    <SelectTrigger id="job-proposal-select">
                      <SelectValue placeholder="Select a job proposal" />
                    </SelectTrigger>
                    <SelectContent>
                      {jobProposals?.map(proposal => (
                        <SelectItem key={proposal.id} value={proposal.id}>
                          {proposal.business_name} - {proposal.service_request_title.substring(0, 30)}... ({proposal.status})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Email Preview */}
            <div className="space-y-4">
              <div className="p-4 bg-muted rounded-lg">
                <h4 className="font-medium mb-2">Subject:</h4>
                <p className="text-sm">{processedEmail.subject}</p>
              </div>

              <div className="p-4 bg-muted rounded-lg">
                <h4 className="font-medium mb-2">From:</h4>
                <p className="text-sm">{processedEmail.sender_email || `${template.sender_name} <${template.local_part}@${currentCommunity?.email_domain || 'community.com'}>`}</p>
              </div>

              <Tabs defaultValue="html" className="w-full">
                <TabsList>
                  <TabsTrigger value="html">HTML Preview</TabsTrigger>
                  <TabsTrigger value="text">Text Version</TabsTrigger>
                </TabsList>
                
                <TabsContent value="html" className="mt-4">
                  <div className="border rounded-lg overflow-hidden">
                    <div 
                      className="w-full min-h-[400px] bg-white"
                      dangerouslySetInnerHTML={{ __html: processedEmail.html }}
                    />
                  </div>
                </TabsContent>
                
                <TabsContent value="text" className="mt-4">
                  <div className="border rounded-lg p-4 bg-white">
                    <pre className="whitespace-pre-wrap text-sm font-mono">
                      {processedEmail.text || 'No text version available'}
                    </pre>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EmailPreview;
